// ============================================================
// TERROR ENGINE — CHALLENGE SYSTEM
// Solrend challenge between players. Works in any MMO zone.
// ============================================================
//
// ⭐ 2026-08-20 — THIS IS THE ONLY IMPLEMENTATION NOW.
//
// It was not. This file was IMPORTED BY NOTHING, and the Plaza carried
// ~173 inline lines doing the same job on a different Firebase path —
// while PRIME §3.7 documented this file as live and the header below
// promised it worked "in Plaza, FactionHall, or any future MMO zone."
// Neither was true. Worse, the two copies had DIVERGED in the only way
// that matters:
//
//   · This one read `profile.activeDeck` / `profile.decks`. Those two
//     fields appear in four places in src/ and are WRITTEN BY NOTHING.
//     So this hook took its `!deck` branch unconditionally, for every
//     account, forever. It could never have started a duel.
//   · The Plaza's copy had been fixed to use `resolveDeck(profile)` and
//     works.
//   · This one guards `setChallengePrompt` against an unchanged target;
//     the Plaza's set a fresh object every 50ms tick — a re-render
//     storm this file had already solved.
//
// So the merge went BOTH WAYS: the Plaza's deck fix comes in, this
// file's identity guard stays, and the Plaza now calls the hook.
//
// ── HOW THE DECK GETS HERE ───────────────────────────────────────────
// It does NOT read the profile. `resolveDeck` is a FUNCTION the zone
// passes in, because where a deck comes from is the zone's business and
// hardcoding it here is precisely how this file went stale the first
// time. The Plaza passes its own resolver, which reads the loadout, the
// sets and the localStorage grimoires in that order.
//
// ── THE PATH — `zones/`, AND I HAD THIS BACKWARDS ────────────────────
// ⚠ CORRECTED 2026-08-20, SAME SESSION, AFTER ACTUALLY READING
// rules.json INSTEAD OF REASONING ABOUT IT.
//
// The first cut of this consolidation moved the path from
// `zones/{zoneId}/challenges/{uid}` to the Plaza's `plaza/challenges`,
// on the argument that `zones` was a NEW TOP-LEVEL NODE forbidden by
// IE_FIREBASE_LAW §2 and that the Plaza's path was the live, permitted
// one. Both halves of that were wrong, and the rules file says so:
//
//   · `zones/$zoneId/challenges/$targetUid` IS IN rules.json, with a
//     purpose-built writer check —
//     `auth != null && (!newData.exists() || newData.child('fromUid').val() === auth.uid)`
//     — which is exactly this feature and nothing else.
//   · `plaza` permits ONLY `players/$uid` and `ready/$portalId/$uid`.
//     There is no `challenges` child and no `.write` on the node
//     itself, so every `plaza/challenges/...` write falls through to
//     the `$other` deny-all. THE PLAZA'S COPY OF THIS FEATURE HAS
//     NEVER BEEN ABLE TO WRITE A SINGLE CHALLENGE IN PRODUCTION.
//
// That is a second, independent reason the proximity duel had never
// started a duel — on top of the `profile.decks` field that does not
// exist. One bug hid the other: the deck check failed first and
// returned before the write that would have been denied.
//
// So the ORIGINAL path was right all along and it is restored. There is
// nothing to migrate, because there is nothing in the old location and
// there never was.
//
// Features:
//   - Proximity detection triggers Solrend pill
//   - 15s auto-expire on sent challenges
//   - Challenger enters as player 0, acceptor as player 1
//   - Decline cleans Firebase node
//
// Usage:
//   const challenge = useChallenge({
//     uid, playerName, zoneId: "plaza",
//     resolveDeck: () => resolveDeck(profile),
//     onEnterGame, showToast,
//   });
//
//   // In the movement loop (outside setPos):
//   challenge.checkProximity(pos, players);
//
//   // In render — or roll your own chrome, as the Plaza does:
//   <ChallengePill {...challenge} onChallenge={challenge.sendChallenge} />
//   <ChallengeModal {...challenge} onAccept={challenge.acceptChallenge}
//                                  onDecline={challenge.declineChallenge} />
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { ref, set, onValue, remove } from "firebase/database";
import { db } from "./firebase";
import { SD } from "./theme";

const CHALLENGE_TTL = 15000; // 15s
const CHALLENGE_RANGE = 2;   // tiles

// The one place a challenge's address is decided. See THE PATH above:
// `plaza` is grandfathered because it is the live, permitted node; a
// new zone goes under the game's own namespace and needs no rules
// deploy, because the $gameId wildcard already covers it.
export const challengePath = (zoneId) => `zones/${zoneId}/challenges`;

// ─────────────────────────────────────────────────────────────
// useChallenge — hook, drop into any MMO zone
// ─────────────────────────────────────────────────────────────
export function useChallenge({ uid, playerName, zoneId = "plaza", resolveDeck, onEnterGame, showToast }) {
  const [challengePrompt, setChallengePrompt]     = useState(null); // { targetUid, targetName }
  const [incomingChallenge, setIncomingChallenge] = useState(null); // { fromUid, fromName, code }

  const base = challengePath(zoneId);

  // ── Listen for incoming challenges ───────────────────────
  useEffect(() => {
    if (!uid) return;
    const challengeRef = ref(db, `${base}/${uid}`);
    const unsub = onValue(challengeRef, snap => {
      const data = snap.val();
      if (!data) { setIncomingChallenge(null); return; }
      if (Date.now() - data.ts > CHALLENGE_TTL) {
        remove(challengeRef); return;
      }
      setIncomingChallenge({ fromUid: data.fromUid, fromName: data.fromName, code: data.code });
    });
    return () => unsub();
  }, [uid, base]);

  // ── Check proximity to other players ─────────────────────
  // Call this in the movement loop OUTSIDE setPos. The identity guard
  // is load-bearing: this runs every 50ms and a fresh object each tick
  // is twenty re-renders a second of a whole room.
  const checkProximity = useCallback((pos, players) => {
    const nearby = Object.entries(players || {}).find(([, p]) =>
      Math.abs(p.x - pos.x) <= CHALLENGE_RANGE &&
      Math.abs(p.y - pos.y) <= CHALLENGE_RANGE
    );
    if (nearby) {
      const [targetUid, targetData] = nearby;
      setChallengePrompt(prev => {
        if (prev?.targetUid === targetUid) return prev;
        return { targetUid, targetName: targetData.name };
      });
    } else {
      setChallengePrompt(prev => (prev === null ? prev : null));
    }
  }, []);

  // ── Send challenge ────────────────────────────────────────
  const sendChallenge = useCallback(async () => {
    if (!challengePrompt || !uid) return;
    const { targetUid, targetName } = challengePrompt;
    setChallengePrompt(null);
    try {
      const { createMatch, drawAndDepleteCards } = await import("./gameManager");
      const deck = resolveDeck ? resolveDeck() : null;
      if (!deck) { showToast?.("No deck found — visit The Forge first", SD.red); return; }
      // drawAndDepleteCards(grimoire, currentHand, count) → { hand, grimoire }.
      const { hand: startingHand, grimoire: depletedGrimoire } = drawAndDepleteCards(deck.grimoire || {}, [], 5);
      const code = await createMatch(
        playerName, depletedGrimoire,
        deck.luxmoire || {}, deck.prophecy || null,
        startingHand, deck.curios || [null, null], deck.esclings || null
      );
      await set(ref(db, `${base}/${targetUid}`), {
        fromUid: uid, fromName: playerName, code, ts: Date.now(),
      });
      showToast?.(`⚔ Challenge sent to ${targetName}...`, SD.purpleLight);
      // Auto-clean after TTL
      setTimeout(() => remove(ref(db, `${base}/${targetUid}`)), CHALLENGE_TTL);
      // Enter as player 0
      onEnterGame?.(code, 0, playerName);
    } catch (e) {
      console.warn("[challenge] send failed:", e && e.message);
      showToast?.("Challenge failed", "#dc2626");
    }
  }, [challengePrompt, uid, playerName, base, resolveDeck, onEnterGame, showToast]);

  // ── Accept challenge ──────────────────────────────────────
  // Seat player 1 via joinMatch BEFORE entering. Without this the match
  // never leaves "waiting" (the board gates its start on players[1] being
  // present) and both clients hang on "Waiting for opponent...".
  const acceptChallenge = useCallback(async () => {
    if (!incomingChallenge || !uid) return;
    const { code, fromName } = incomingChallenge;
    try {
      const { joinMatch, drawAndDepleteCards } = await import("./gameManager");
      const deck = resolveDeck ? resolveDeck() : null;
      if (!deck) { showToast?.("No deck found — visit The Forge first", SD.red); return; }
      const { hand: startingHand, grimoire: depletedGrimoire } =
        drawAndDepleteCards(deck.grimoire || {}, [], 5);
      const joined = await joinMatch(
        code, playerName, depletedGrimoire,
        deck.luxmoire || {}, deck.prophecy || null,
        startingHand, deck.curios || [null, null], deck.esclings || null
      );
      // { error } | { success } — always a truthy object; inspect the shape.
      if (joined?.error) { showToast?.(joined.error, SD.red); return; }
      remove(ref(db, `${base}/${uid}`));
      setIncomingChallenge(null);
      showToast?.(`⚔ Entering Solrend vs ${fromName}...`, SD.purpleLight);
      // ⚠ SEAT 1 WAS A LIE THE MOMENT A THIRD CHAIR EXISTED — the same
      // bug Plaza already fixed, still sitting here. joinMatch hands back
      // the seat it ACTUALLY claimed two lines up and this threw it away.
      // Correct today only because a challenge always creates a 2-seat
      // match; the day a challenge can be an FFA, this sits the joiner in
      // somebody else's chair.
      onEnterGame?.(code, Number.isInteger(joined?.seatIdx) ? joined.seatIdx : 1, playerName);
    } catch (e) {
      console.warn("[challenge] accept failed:", e && e.message);
      showToast?.("Failed to join challenge", SD.red);
    }
  }, [incomingChallenge, uid, base, playerName, resolveDeck, onEnterGame, showToast]);

  // ── Decline challenge ─────────────────────────────────────
  const declineChallenge = useCallback(() => {
    remove(ref(db, `${base}/${uid}`));
    setIncomingChallenge(null);
  }, [uid, base]);

  const dismissPrompt = useCallback(() => setChallengePrompt(null), []);

  return {
    challengePrompt, incomingChallenge,
    checkProximity, sendChallenge,
    acceptChallenge, declineChallenge,
    dismissPrompt,
  };
}

// ─────────────────────────────────────────────────────────────
// THE DEFAULT CHROME
// ─────────────────────────────────────────────────────────────
// A zone may render its own instead — the Plaza does, because its pill
// lives inside a DockPortal and is portrait-aware and neither of those
// belongs in a shared component. The LOGIC is what had to stop being
// duplicated; the markup is allowed to differ per room. These two are
// what a zone gets for free if it does not care.
// ChallengePill — Solrend button shown when near another player
// ─────────────────────────────────────────────────────────────
export function ChallengePill({ challengePrompt, onChallenge, onDismiss, mobile = false }) {
  if (!challengePrompt) return null;
  return (
    <div style={{
      position: "fixed",
      left: "50%",
      bottom: mobile ? 120 : 80,
      transform: "translateX(-50%)",
      zIndex: 160,
      display: "flex", gap: 8, alignItems: "center",
    }}>
      <button onClick={onChallenge} style={{
        padding: "10px 24px", borderRadius: 24,
        background: `linear-gradient(135deg, ${SD.purpleDim}, #3b0f6e)`,
        border: `1px solid ${SD.purpleLight}`,
        color: SD.starWhite, fontFamily: SD.fontDisplay,
        fontSize: 13, fontWeight: 900, letterSpacing: 2,
        cursor: "pointer",
        animation: "fightPulse 1s ease-in-out infinite alternate",
        boxShadow: `0 0 20px ${SD.purpleLight}66`,
      }}>
        ⚔ Solrend · {challengePrompt.targetName}
      </button>
      <button aria-label="Dismiss" onClick={onDismiss} style={{
        padding: "8px 14px", borderRadius: 20,
        background: "transparent", border: `1px solid ${SD.void4}`,
        color: SD.starDim, fontFamily: SD.fontDisplay,
        fontSize: 11, cursor: "pointer",
      }}>✕</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ChallengeModal — incoming challenge full-screen prompt
// ─────────────────────────────────────────────────────────────
export function ChallengeModal({ incomingChallenge, onAccept, onDecline }) {
  if (!incomingChallenge) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
    }}>
      <div style={{
        background: `linear-gradient(145deg, ${SD.void2}, ${SD.void1})`,
        border: `1px solid ${SD.purpleLight}`,
        borderRadius: 16, padding: "32px 40px",
        textAlign: "center", fontFamily: SD.fontDisplay,
        boxShadow: `0 0 40px ${SD.purpleLight}44`,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 20,
      }}>
        <div style={{ fontSize: 11, color: SD.purpleLight, letterSpacing: 3 }}>
          INCOMING CHALLENGE
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: SD.starWhite, fontStyle: "italic" }}>
          ⚔ {incomingChallenge.fromName}
        </div>
        <div style={{ fontSize: 11, color: SD.starDim }}>
          challenges you to a Solrend
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={onAccept} style={{
            padding: "12px 28px", borderRadius: 12,
            background: `linear-gradient(135deg, ${SD.purpleDim}, #3b0f6e)`,
            border: `1px solid ${SD.purpleLight}`,
            color: SD.starWhite, fontFamily: SD.fontDisplay,
            fontSize: 13, fontWeight: 900, cursor: "pointer",
            boxShadow: `0 0 16px ${SD.purpleLight}44`,
          }}>Accept</button>
          <button onClick={onDecline} style={{
            padding: "12px 28px", borderRadius: 12,
            background: "transparent", border: `1px solid ${SD.void4}`,
            color: SD.starDim, fontFamily: SD.fontDisplay,
            fontSize: 13, cursor: "pointer",
          }}>Decline</button>
        </div>
      </div>
    </div>
  );
}
